// User Management page — admin-only. List, role-update, activate/deactivate users.

import { useState, useMemo, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import { useUsers, updateUser, deactivateUser } from "../hooks/useUsers";
import type { UserRow } from "../hooks/useUsers";
import GroupsAndPermissions from "../components/users/GroupsAndPermissions";

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "Never";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function ShieldCheckIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function UserIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function DotsIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

function SearchIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function RefreshIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
      <path d="M16 21h5v-5" />
    </svg>
  );
}

function XIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function WarningIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sublabel,
  accent,
}: {
  label: string;
  value: number | string;
  sublabel?: string;
  accent?: "green" | "blue" | "amber" | "red";
}) {
  const accentClass =
    accent === "green"
      ? "text-emerald-600 dark:text-emerald-400"
      : accent === "blue"
        ? "text-blue-600 dark:text-blue-400"
        : accent === "amber"
          ? "text-amber-600 dark:text-amber-400"
          : accent === "red"
            ? "text-red-600 dark:text-red-400"
            : "text-theme-primary";

  return (
    <div className="bg-theme-raised border border-theme-subtle rounded-xl p-5 flex flex-col gap-1">
      <span className="text-xs font-semibold text-theme-muted uppercase tracking-wider">
        {label}
      </span>
      <span className={`text-3xl font-bold tabular-nums ${accentClass}`}>
        {value}
      </span>
      {sublabel && <span className="text-xs text-theme-muted">{sublabel}</span>}
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────────────────────

function UserAvatar({ name, size = 32 }: { name: string; size?: number }) {
  return (
    <div
      className="rounded-full bg-dcvfd-accent flex items-center justify-center shrink-0 text-white font-semibold select-none"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials(name)}
    </div>
  );
}

// ── Role Badge ────────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: "admin" | "viewer" | "board_member" }) {
  if (role === "admin") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
        <ShieldCheckIcon size={11} />
        Admin
      </span>
    );
  }
  if (role === "board_member") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">
        <ShieldCheckIcon size={11} />
        Board / Sr. Staff
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/30">
      <UserIcon size={11} />
      Viewer
    </span>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────

function StatusBadge({ isActive }: { isActive: boolean }) {
  if (isActive) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/25">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" />
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-zinc-500/10 text-zinc-500 dark:text-zinc-400 border border-zinc-500/25">
      <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
      Inactive
    </span>
  );
}

// ── Skeleton Row ──────────────────────────────────────────────────────────────

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i} className="border-t border-theme-subtle">
          <td className="px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="skeleton w-8 h-8 rounded-full" />
              <div className="skeleton h-4 w-32 rounded" />
            </div>
          </td>
          <td className="px-4 py-3">
            <div className="skeleton h-4 w-40 rounded" />
          </td>
          <td className="px-4 py-3">
            <div className="skeleton h-5 w-16 rounded-full" />
          </td>
          <td className="px-4 py-3">
            <div className="skeleton h-5 w-16 rounded-full" />
          </td>
          <td className="px-4 py-3">
            <div className="skeleton h-4 w-20 rounded" />
          </td>
          <td className="px-4 py-3">
            <div className="skeleton h-4 w-24 rounded" />
          </td>
          <td className="px-4 py-3">
            <div className="skeleton h-4 w-24 rounded" />
          </td>
          <td className="px-4 py-3">
            <div className="skeleton h-6 w-8 rounded" />
          </td>
        </tr>
      ))}
    </>
  );
}

// ── Actions Dropdown ──────────────────────────────────────────────────────────

function ActionsMenu({
  user,
  currentUserId,
  onChangeRole,
  onDeactivate,
  onReactivate,
}: {
  user: UserRow;
  currentUserId: number;
  onChangeRole: (user: UserRow) => void;
  onDeactivate: (user: UserRow) => void;
  onReactivate: (user: UserRow) => void;
}) {
  const [open, setOpen] = useState(false);
  const isSelf = user.id === currentUserId;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded-md text-theme-muted hover:text-theme-primary hover:bg-theme-overlay transition-colors"
        title="Actions"
        aria-label="User actions"
      >
        <DotsIcon />
      </button>

      {open && (
        <>
          {/* Backdrop to close on outside click */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-44 bg-theme-raised border border-theme-default rounded-lg shadow-lg overflow-hidden">
            <button
              className="w-full text-left px-4 py-2.5 text-sm text-theme-secondary hover:bg-theme-overlay hover:text-theme-primary transition-colors"
              onClick={() => {
                setOpen(false);
                onChangeRole(user);
              }}
            >
              Change Role
            </button>
            {user.is_active === 1 ? (
              <button
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  isSelf
                    ? "text-theme-muted opacity-40 cursor-not-allowed"
                    : "text-red-600 dark:text-red-400 hover:bg-red-500/10"
                }`}
                disabled={isSelf}
                onClick={() => {
                  if (!isSelf) {
                    setOpen(false);
                    onDeactivate(user);
                  }
                }}
                title={isSelf ? "Cannot deactivate yourself" : undefined}
              >
                Deactivate
              </button>
            ) : (
              <button
                className="w-full text-left px-4 py-2.5 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                onClick={() => {
                  setOpen(false);
                  onReactivate(user);
                }}
              >
                Reactivate
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Role Change Modal ─────────────────────────────────────────────────────────

const ROLE_OPTIONS: {
  value: "viewer" | "board_member" | "admin";
  label: string;
}[] = [
  { value: "viewer", label: "Viewer" },
  { value: "board_member", label: "Board / Sr. Staff" },
  { value: "admin", label: "Admin" },
];

function RoleChangeModal({
  user,
  isLastAdmin,
  isSelf,
  onConfirm,
  onCancel,
  saving,
  error,
}: {
  user: UserRow;
  /** true when this user is the only remaining active admin. */
  isLastAdmin: boolean;
  isSelf: boolean;
  onConfirm: (newRole: "admin" | "viewer" | "board_member") => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}) {
  const [selected, setSelected] = useState<"admin" | "viewer" | "board_member">(
    user.role,
  );
  const isDemotion = user.role === "admin" && selected !== "admin";
  const isPromotion = user.role !== "admin" && selected === "admin";
  const unchanged = selected === user.role;
  // Guard: blocking a change that would strip the last active admin, or self-demotion.
  const lockoutBlocked = isDemotion && (isLastAdmin || isSelf);

  return (
    <ModalOverlay onClose={onCancel}>
      <div className="bg-theme-raised border border-theme-default rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-theme-primary">
            Change Role
          </h2>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-md text-theme-muted hover:bg-theme-overlay hover:text-theme-primary transition-colors"
          >
            <XIcon />
          </button>
        </div>

        <div className="flex items-center gap-3 mb-5 p-3 bg-theme-overlay rounded-lg">
          <UserAvatar name={user.name} size={36} />
          <div>
            <p className="font-medium text-theme-primary text-sm">
              {user.name}
            </p>
            <p className="text-xs text-theme-muted">{user.email}</p>
          </div>
        </div>

        <div className="mb-1">
          <label className="block text-xs font-semibold text-theme-muted uppercase tracking-wider mb-2">
            Current Role
          </label>
          <RoleBadge role={user.role} />
        </div>

        <div className="mt-4 mb-5">
          <label className="block text-xs font-semibold text-theme-muted uppercase tracking-wider mb-2">
            New Role
          </label>
          <div className="grid grid-cols-3 gap-3">
            {ROLE_OPTIONS.map(({ value: r, label }) => (
              <button
                key={r}
                onClick={() => setSelected(r)}
                className={`py-2.5 px-3 rounded-lg border text-sm font-medium transition-colors ${
                  selected === r
                    ? r === "admin"
                      ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
                      : r === "board_member"
                        ? "bg-amber-500/15 border-amber-500/40 text-amber-700 dark:text-amber-400"
                        : "bg-blue-500/15 border-blue-500/40 text-blue-700 dark:text-blue-400"
                    : "border-theme-subtle text-theme-secondary hover:bg-theme-overlay"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {lockoutBlocked && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-400 text-sm mb-4">
            <WarningIcon size={16} />
            <p>
              {isSelf
                ? "You cannot remove your own admin access. Have another admin do it."
                : "This is the only active admin. Promote another user to admin before downgrading this one."}
            </p>
          </div>
        )}
        {isDemotion && !lockoutBlocked && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-sm mb-4">
            <WarningIcon size={16} />
            <p>
              Downgrading this admin removes their access to admin-only pages
              and actions.
            </p>
          </div>
        )}
        {isPromotion && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 text-sm mb-4">
            <ShieldCheckIcon size={16} />
            <p>
              This user will gain full admin access including User Management
              and Sync controls.
            </p>
          </div>
        )}
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-theme-secondary hover:bg-theme-overlay transition-colors"
          >
            Cancel
          </button>
          <button
            disabled={unchanged || saving || lockoutBlocked}
            onClick={() => onConfirm(selected)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              unchanged || saving || lockoutBlocked
                ? "bg-theme-overlay text-theme-muted cursor-not-allowed"
                : "bg-dcvfd text-white hover:bg-dcvfd-light"
            }`}
          >
            {saving ? "Saving…" : "Confirm"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ── Deactivate Modal ──────────────────────────────────────────────────────────

function DeactivateModal({
  user,
  onConfirm,
  onCancel,
  saving,
  error,
}: {
  user: UserRow;
  onConfirm: () => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}) {
  return (
    <ModalOverlay onClose={onCancel}>
      <div className="bg-theme-raised border border-theme-default rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-theme-primary">
            Deactivate User
          </h2>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-md text-theme-muted hover:bg-theme-overlay hover:text-theme-primary transition-colors"
          >
            <XIcon />
          </button>
        </div>

        <div className="flex items-center gap-3 mb-5 p-3 bg-theme-overlay rounded-lg">
          <UserAvatar name={user.name} size={36} />
          <div>
            <p className="font-medium text-theme-primary text-sm">
              {user.name}
            </p>
            <p className="text-xs text-theme-muted">{user.email}</p>
          </div>
        </div>

        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-400 text-sm mb-5">
          <WarningIcon size={16} />
          <p>
            Deactivating this account preserves all history and data but
            immediately revokes access. The user can be reactivated at any time.
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-theme-secondary hover:bg-theme-overlay transition-colors"
          >
            Cancel
          </button>
          <button
            disabled={saving}
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              saving
                ? "bg-red-400 cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700"
            } text-white`}
          >
            {saving ? "Deactivating…" : "Deactivate"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ── Modal overlay wrapper ─────────────────────────────────────────────────────

function ModalOverlay({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md">{children}</div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type ModalState =
  | { type: "none" }
  | { type: "changeRole"; user: UserRow }
  | { type: "deactivate"; user: UserRow };

type RoleFilter = "all" | "admin" | "board_member" | "viewer";
type StatusFilter = "all" | "active" | "inactive";
type SortKey = "name" | "email" | "role" | "last_login_at" | "created_at";
type SortDir = "asc" | "desc";

const PAGE_SIZE = 25;

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const toast = useToast();
  const { data, isLoading, error, refresh } = useUsers();

  // ── Filters / search / sort / pagination ─────────────────────────
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("last_login_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(0);

  // ── Modal + mutation state ────────────────────────────────────────
  const [modal, setModal] = useState<ModalState>({ type: "none" });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // ── Filtered + sorted + paginated list ───────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (data?.users ?? [])
      .filter((u) => {
        if (roleFilter !== "all" && u.role !== roleFilter) return false;
        if (statusFilter === "active" && u.is_active !== 1) return false;
        if (statusFilter === "inactive" && u.is_active !== 0) return false;
        if (
          q &&
          !u.name.toLowerCase().includes(q) &&
          !u.email.toLowerCase().includes(q)
        )
          return false;
        return true;
      })
      .sort((a, b) => {
        let valA: string | number | null = null;
        let valB: string | number | null = null;
        if (sortKey === "name") {
          valA = a.name;
          valB = b.name;
        } else if (sortKey === "email") {
          valA = a.email;
          valB = b.email;
        } else if (sortKey === "role") {
          valA = a.role;
          valB = b.role;
        } else if (sortKey === "last_login_at") {
          valA = a.last_login_at ?? "";
          valB = b.last_login_at ?? "";
        } else if (sortKey === "created_at") {
          valA = a.created_at ?? "";
          valB = b.created_at ?? "";
        }
        if (valA == null) return 1;
        if (valB == null) return -1;
        const cmp = valA < valB ? -1 : valA > valB ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      });
  }, [data, search, roleFilter, statusFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(
    page * PAGE_SIZE,
    page * PAGE_SIZE + PAGE_SIZE,
  );

  // ── Mutation handlers ─────────────────────────────────────────────
  const handleRoleConfirm = useCallback(
    async (newRole: "admin" | "viewer" | "board_member") => {
      if (modal.type !== "changeRole") return;
      setSaving(true);
      setModalError(null);
      try {
        await updateUser(modal.user.id, { role: newRole });
        setModal({ type: "none" });
        toast.success(`Role updated to ${newRole.replace("_", " ")}`);
        refresh();
      } catch (e) {
        setModalError(e instanceof Error ? e.message : "Failed to update role");
      } finally {
        setSaving(false);
      }
    },
    [modal, refresh, toast],
  );

  const handleDeactivateConfirm = useCallback(async () => {
    if (modal.type !== "deactivate") return;
    setSaving(true);
    setModalError(null);
    try {
      const name = modal.user.name || modal.user.email;
      await deactivateUser(modal.user.id);
      setModal({ type: "none" });
      toast.success(`Deactivated ${name}`);
      refresh();
    } catch (e) {
      setModalError(
        e instanceof Error ? e.message : "Failed to deactivate user",
      );
    } finally {
      setSaving(false);
    }
  }, [modal, refresh, toast]);

  const handleReactivate = useCallback(
    async (user: UserRow) => {
      try {
        await updateUser(user.id, { is_active: true });
        toast.success(`Reactivated ${user.name || user.email}`);
        refresh();
      } catch (e) {
        // Previously failed silently (console.error only) — surface it.
        toast.error(
          e instanceof Error
            ? `Reactivate failed: ${e.message}`
            : "Failed to reactivate user",
        );
      }
    },
    [refresh, toast],
  );

  // ── Access guard (after all hooks) ───────────────────────────────
  if (currentUser?.role !== "admin") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center p-8">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
          <ShieldCheckIcon size={32} />
        </div>
        <h2 className="text-xl font-semibold text-theme-primary">
          Access Denied
        </h2>
        <p className="text-theme-muted max-w-sm">
          User Management is restricted to admins. Contact an administrator to
          request access.
        </p>
      </div>
    );
  }

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  }

  function SortIndicator({ col }: { col: SortKey }) {
    if (sortKey !== col)
      return <span className="ml-1 text-theme-muted opacity-30">↕</span>;
    return <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>;
  }

  const openChangeRole = (user: UserRow) => {
    setModalError(null);
    setModal({ type: "changeRole", user });
  };

  const openDeactivate = (user: UserRow) => {
    setModalError(null);
    setModal({ type: "deactivate", user });
  };

  const stats = data?.stats;

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8 space-y-8">
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            User Management
          </h1>
          <p className="text-sm text-theme-muted mt-1">
            Manage access and roles for the DCVFD Data Platform
          </p>
        </div>
        <button
          onClick={refresh}
          className="self-start sm:self-auto flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-theme-secondary hover:bg-theme-overlay border border-theme-subtle transition-colors"
        >
          <RefreshIcon />
          Refresh
        </button>
      </div>

      {/* ── Groups + Permission matrix (Entra-driven) ───────────── */}
      <GroupsAndPermissions />

      {/* ── KPI Row ───────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-theme-raised border border-theme-subtle rounded-xl p-5"
            >
              <div className="skeleton h-3 w-20 rounded mb-3" />
              <div className="skeleton h-8 w-12 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Total Users"
            value={stats?.total ?? 0}
            sublabel="All accounts"
          />
          <KpiCard
            label="Admin Users"
            value={stats?.admins ?? 0}
            accent="green"
            sublabel="With admin role"
          />
          <KpiCard
            label="Active (30d)"
            value={stats?.active_last_30d ?? 0}
            accent="blue"
            sublabel="Logged in recently"
          />
          <KpiCard
            label="Inactive"
            value={stats?.inactive ?? 0}
            accent="red"
            sublabel="Deactivated accounts"
          />
        </div>
      )}

      {/* ── Error state ───────────────────────────────────────────── */}
      {error && !isLoading && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-400">
          <WarningIcon />
          <div>
            <p className="font-medium text-sm">Failed to load users</p>
            <p className="text-xs opacity-75">{error}</p>
          </div>
          <button
            onClick={refresh}
            className="ml-auto px-3 py-1.5 rounded-lg text-xs font-medium border border-red-500/40 hover:bg-red-500/15 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Table Card ───────────────────────────────────────────── */}
      <div className="bg-theme-raised border border-theme-subtle rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-theme-subtle">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted pointer-events-none">
              <SearchIcon />
            </span>
            <input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="w-full pl-9 pr-4 py-2 text-sm rounded-lg bg-theme-surface border border-theme-subtle text-theme-primary placeholder:text-theme-muted focus:outline-none focus:border-dcvfd-accent transition-colors"
            />
          </div>

          {/* Role filter */}
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as RoleFilter);
              setPage(0);
            }}
            className="px-3 py-2 text-sm rounded-lg bg-theme-surface border border-theme-subtle text-theme-secondary focus:outline-none focus:border-dcvfd-accent transition-colors cursor-pointer"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="board_member">Board / Sr. Staff</option>
            <option value="viewer">Viewer</option>
          </select>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter);
              setPage(0);
            }}
            className="px-3 py-2 text-sm rounded-lg bg-theme-surface border border-theme-subtle text-theme-secondary focus:outline-none focus:border-dcvfd-accent transition-colors cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <span className="text-xs text-theme-muted ml-auto whitespace-nowrap">
            {filtered.length} user{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Table — desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">
              User accounts with role, status, last login, auth method, and
              actions
            </caption>
            <thead>
              <tr className="border-b border-theme-subtle text-xs text-theme-muted uppercase tracking-wider">
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold cursor-pointer select-none hover:text-theme-primary transition-colors whitespace-nowrap"
                  onClick={() => handleSort("name")}
                >
                  User <SortIndicator col="name" />
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold cursor-pointer select-none hover:text-theme-primary transition-colors"
                  onClick={() => handleSort("email")}
                >
                  Email <SortIndicator col="email" />
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold cursor-pointer select-none hover:text-theme-primary transition-colors"
                  onClick={() => handleSort("role")}
                >
                  Role <SortIndicator col="role" />
                </th>
                <th scope="col" className="px-4 py-3 text-left font-semibold">
                  Status
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold cursor-pointer select-none hover:text-theme-primary transition-colors whitespace-nowrap"
                  onClick={() => handleSort("last_login_at")}
                >
                  Last Login <SortIndicator col="last_login_at" />
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold whitespace-nowrap"
                >
                  Auth Method
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold cursor-pointer select-none hover:text-theme-primary transition-colors"
                  onClick={() => handleSort("created_at")}
                >
                  Joined <SortIndicator col="created_at" />
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left font-semibold w-10"
                >
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows />
              ) : paginated.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-16 text-center text-theme-muted"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <UserIcon size={32} />
                      <p className="font-medium">No users found</p>
                      {(search ||
                        roleFilter !== "all" ||
                        statusFilter !== "all") && (
                        <p className="text-xs">Try adjusting your filters</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((user) => (
                  <tr
                    key={user.id}
                    className={`hover:bg-theme-overlay/50 transition-colors ${
                      user.is_active === 0 ? "opacity-60" : ""
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <UserAvatar name={user.name} size={32} />
                        <span className="font-medium text-theme-primary truncate max-w-[160px]">
                          {user.name}
                          {user.id === currentUser?.userId && (
                            <span className="ml-1.5 text-xs text-theme-muted">
                              (you)
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-theme-secondary font-mono text-xs">
                      {user.email}
                    </td>
                    <td className="px-4 py-3">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge isActive={user.is_active === 1} />
                    </td>
                    <td className="px-4 py-3 text-theme-muted tabular-nums text-xs whitespace-nowrap">
                      {relativeTime(user.last_login_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-sky-500/10 text-sky-700 dark:text-sky-400 border border-sky-500/25">
                        Entra SSO
                      </span>
                    </td>
                    <td className="px-4 py-3 text-theme-muted text-xs tabular-nums whitespace-nowrap">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <ActionsMenu
                        user={user}
                        currentUserId={currentUser?.userId ?? -1}
                        onChangeRole={openChangeRole}
                        onDeactivate={openDeactivate}
                        onReactivate={handleReactivate}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Cards — mobile */}
        <div className="md:hidden divide-y divide-theme-subtle/40">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="skeleton w-10 h-10 rounded-full shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-4 w-32 rounded" />
                    <div className="skeleton h-3 w-48 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : paginated.length === 0 ? (
            <div className="py-16 flex flex-col items-center gap-2 text-theme-muted">
              <UserIcon size={32} />
              <p className="font-medium">No users found</p>
            </div>
          ) : (
            paginated.map((user) => (
              <div
                key={user.id}
                className={`p-4 ${user.is_active === 0 ? "opacity-60" : ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <UserAvatar name={user.name} size={40} />
                    <div className="min-w-0">
                      <p className="font-medium text-theme-primary text-sm truncate">
                        {user.name}
                        {user.id === currentUser?.userId && (
                          <span className="ml-1.5 text-xs text-theme-muted">
                            (you)
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-theme-muted font-mono truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <ActionsMenu
                    user={user}
                    currentUserId={currentUser?.userId ?? -1}
                    onChangeRole={openChangeRole}
                    onDeactivate={openDeactivate}
                    onReactivate={handleReactivate}
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2 items-center text-xs text-theme-muted">
                  <RoleBadge role={user.role} />
                  <StatusBadge isActive={user.is_active === 1} />
                  <span>Last login: {relativeTime(user.last_login_at)}</span>
                  <span>Joined: {formatDate(user.created_at)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {!isLoading && filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-theme-subtle">
            <span className="text-xs text-theme-muted">
              Showing {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, filtered.length)} of{" "}
              {filtered.length}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-theme-subtle text-theme-secondary hover:bg-theme-overlay disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-theme-subtle text-theme-secondary hover:bg-theme-overlay disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ───────────────────────────────────────────────── */}
      {modal.type === "changeRole" && (
        <RoleChangeModal
          user={modal.user}
          isLastAdmin={
            modal.user.role === "admin" &&
            (data?.users ?? []).filter(
              (u) => u.role === "admin" && u.is_active === 1,
            ).length <= 1
          }
          isSelf={modal.user.id === currentUser?.userId}
          onConfirm={handleRoleConfirm}
          onCancel={() => setModal({ type: "none" })}
          saving={saving}
          error={modalError}
        />
      )}
      {modal.type === "deactivate" && (
        <DeactivateModal
          user={modal.user}
          onConfirm={handleDeactivateConfirm}
          onCancel={() => setModal({ type: "none" })}
          saving={saving}
          error={modalError}
        />
      )}
    </div>
  );
}
